import { Check, Eye, LoaderCircle, Mail, Pencil, Send } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { StatusBadge } from '../../components/ui/common';
import { datetime, scheduleDescription } from '../../lib/format';
import type { Schedule } from './schedule-form';

export interface DeliverySummary { id: string; status: string }
export const deliveryPending = (delivery?: DeliverySummary) => delivery?.status === 'queued' || delivery?.status === 'sending';

export function ScheduleCard({ schedule, canEdit, shadow, previewing, previewPending, preparing, queuePending, toggling, delivery, onPreview, onEdit, onToggle, onSend }: {
  schedule: Schedule; canEdit: boolean; shadow: boolean; previewing: boolean; previewPending: boolean;
  preparing: boolean; queuePending: boolean; toggling: boolean; delivery?: DeliverySummary;
  onPreview: () => void; onEdit: () => void; onToggle: () => void; onSend: () => void;
}) {
  const sending = deliveryPending(delivery);
  const sent = delivery?.status === 'sent';
  const previewed = delivery?.status === 'previewed';
  const busy = previewing || preparing || sending;
  const sendLabel = preparing ? 'Preparing…' : sending ? 'Sending…' : sent ? 'Sent' : previewed ? 'Previewed' : shadow ? 'Preview delivery' : 'Send now';
  const feedback = preparing ? 'Preparing report… This may take a few seconds.'
    : sending ? 'Sending report… Follow its progress in delivery history.'
    : previewing ? 'Preparing preview… This may take a few seconds.'
    : sent ? 'Sent — Microsoft accepted the email for delivery.'
    : previewed ? 'Delivery preview recorded only.'
    : delivery?.status === 'uncertain' ? 'Delivery uncertain. Check Sent Items before sending another copy.'
    : delivery?.status === 'failed' || delivery?.status === 'blocked' ? 'Not sent. Review delivery history for details.' : '';
  return <article className="schedule-card">
    <div className="schedule-symbol"><Mail size={22} /></div>
    <div className="schedule-details"><h3>{schedule.name}</h3><p>{schedule.recipient}</p><span>{scheduleDescription(schedule.cron)} · {schedule.timezone}</span>{schedule.enabled && schedule.nextRunAt && <small>Next run {datetime(schedule.nextRunAt)}</small>}</div>
    <StatusBadge status={schedule.enabled ? 'active' : 'paused'} />
    <div className="schedule-actions">
      <div className="button-row">
        <Button size="sm" variant="outline" onClick={onPreview} disabled={previewPending || preparing || sending} aria-busy={previewing}>
          {previewing ? <LoaderCircle aria-hidden="true" className="animate-spin motion-reduce:animate-none" size={14} /> : <Eye aria-hidden="true" size={14} />}{previewing ? 'Preparing preview…' : 'Preview'}
        </Button>
        {canEdit && <>
          <Button size="icon" variant="ghost" aria-label={`Edit ${schedule.name}`} onClick={onEdit} disabled={busy}><Pencil size={16} /></Button>
          <Button size="sm" variant="outline" onClick={onToggle} disabled={toggling || busy}>{schedule.enabled ? 'Pause' : 'Enable'}</Button>
          <Button size="sm" onClick={onSend} disabled={queuePending || busy || sent || previewed} aria-busy={preparing || sending}>
            {preparing || sending ? <LoaderCircle aria-hidden="true" className="animate-spin motion-reduce:animate-none" size={14} /> : sent || previewed ? <Check aria-hidden="true" size={14} /> : <Send aria-hidden="true" size={14} />}{sendLabel}
          </Button>
          {(sent || previewed) && <Button size="sm" variant="ghost" onClick={onSend} disabled={queuePending || busy}>{previewed ? 'Preview again' : 'Send again'}</Button>}
        </>}
      </div>
      <p className={`schedule-feedback ${delivery?.status === 'failed' || delivery?.status === 'blocked' ? 'negative' : 'muted'}`} role="status" aria-live="polite" aria-atomic="true">{feedback}</p>
    </div>
  </article>;
}
